import { InstancedMesh, Matrix4 } from 'three';
import type { BufferGeometry, Material, Object3D } from 'three';

export interface InstanceHandle {
  slot: number;
}

const tmp = new Matrix4();

/**
 * A growable, always-dense InstancedMesh. One draw call per prefab part regardless of how many
 * copies exist; removal swaps the last instance into the hole so the GPU never draws empties.
 * Only the slots that changed are re-uploaded, and an empty pool is skipped entirely. Pools ring
 * the camera, so a bounding sphere would never cull anything: frustum culling is off.
 */
export class InstancePool {
  mesh: InstancedMesh;
  private handles: InstanceHandle[] = [];
  private dirty = false;
  /** Changed slot range since the last flush. */
  private lo = Infinity;
  private hi = -1;
  private readonly parent: Object3D;
  private readonly geometry: BufferGeometry;
  private readonly material: Material;
  private readonly depthMaterial: Material | undefined;
  private readonly castShadow: boolean;

  constructor(parent: Object3D, geometry: BufferGeometry, material: Material, castShadow: boolean, depthMaterial?: Material, capacity = 16) {
    this.parent = parent;
    this.geometry = geometry;
    this.material = material;
    this.depthMaterial = depthMaterial;
    this.castShadow = castShadow;
    this.mesh = this.create(capacity);
    parent.add(this.mesh);
  }

  private create(capacity: number): InstancedMesh {
    const m = new InstancedMesh(this.geometry, this.material, capacity);
    m.count = 0;
    m.visible = false;
    m.frustumCulled = false;
    m.castShadow = this.castShadow;
    m.receiveShadow = true;
    if (this.depthMaterial) m.customDepthMaterial = this.depthMaterial;
    return m;
  }

  private grow(): void {
    const old = this.mesh;
    const next = this.create(old.instanceMatrix.count * 2);
    (next.instanceMatrix.array as Float32Array).set(old.instanceMatrix.array as Float32Array);
    next.count = old.count;
    next.visible = old.visible;
    next.renderOrder = old.renderOrder;
    this.parent.remove(old);
    old.dispose();
    this.parent.add(next);
    this.mesh = next;
  }

  private touch(slot: number): void {
    if (slot < this.lo) this.lo = slot;
    if (slot > this.hi) this.hi = slot;
    this.dirty = true;
  }

  add(matrix: Matrix4): InstanceHandle {
    const n = this.handles.length;
    if (n >= this.mesh.instanceMatrix.count) this.grow();
    const h: InstanceHandle = { slot: n };
    this.handles.push(h);
    this.mesh.setMatrixAt(n, matrix);
    this.mesh.count = n + 1;
    this.touch(n);
    return h;
  }

  remove(h: InstanceHandle): void {
    const last = this.handles.length - 1;
    if (h.slot < 0 || h.slot > last) return;
    if (h.slot !== last) {
      this.mesh.getMatrixAt(last, tmp);
      this.mesh.setMatrixAt(h.slot, tmp);
      this.touch(h.slot);
      const moved = this.handles[last]!;
      moved.slot = h.slot;
      this.handles[h.slot] = moved;
    }
    this.handles.pop();
    h.slot = -1;
    this.mesh.count = this.handles.length;
    this.dirty = true;
  }

  get count(): number {
    return this.handles.length;
  }

  /** Shader pre-compilation only sees visible meshes: show empty pools while it runs. */
  warmup(on: boolean): void {
    this.mesh.visible = on || this.mesh.count > 0;
  }

  /** Upload the changed slots once per frame, only if something changed. */
  flush(): void {
    if (!this.dirty) return;
    this.dirty = false;
    const m = this.mesh;
    m.visible = m.count > 0;
    const hi = Math.min(this.hi, m.count - 1);
    if (hi >= this.lo) {
      const attr = m.instanceMatrix;
      attr.clearUpdateRanges();
      attr.addUpdateRange(this.lo * 16, (hi - this.lo + 1) * 16);
      attr.needsUpdate = true;
    }
    this.lo = Infinity;
    this.hi = -1;
  }

  dispose(): void {
    this.parent.remove(this.mesh);
    this.mesh.dispose();
  }
}
